import { useCallback } from 'react';
import type { Location, Path } from 'react-router';
import { generatePath, useLocation } from 'react-router';

import type { To } from 'platform/router/react';
import type { IQualificationContext } from 'shared/helpers/QualificationContext';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { ADDRESS_UUID_PARAM } from 'shared/helpers/routes';

import { DevError } from './Error';

const DEFAULT_DISALLOWED_SEARCH_PARAMS = ['access_token', 'show_offer_refresh', 'refresh_offer'];

type PathWithPathname = Omit<Partial<Path>, 'pathname'> & { pathname: string };

/**
 * Parses a url string into a location object
 * @internal
 */
export const parseUrlString = (url: string): PathWithPathname => {
  const hashSplitPath = url.split('#');
  const hash = hashSplitPath.length > 1 ? `#${hashSplitPath[1]}` : undefined;
  // Temporarily remove optional URL param to ease url parsing
  const splitPath =
    hashSplitPath[0].replace(`${ADDRESS_UUID_PARAM}?`, ADDRESS_UUID_PARAM).split('?') ?? [];
  // Re-add optional URL param
  const pathname = splitPath[0]
    .replace(ADDRESS_UUID_PARAM, `${ADDRESS_UUID_PARAM}?`)
    .replace('??', '?');
  const toSearchString = splitPath.length > 1 ? `?${splitPath[1]}` : undefined;
  return {
    pathname,
    // A URL search string, beginning with a ?.
    search: toSearchString,
    hash,
  };
};

/**
 * Returns a location object that can be used to navigate to a route INTERNAL to the SPA
 * @throws Error if the `to` prop is not a string or a location object
 * @internal
 */
export const getToPath = (
  currentLocation: Location,
  nextLocation: Partial<Path>,
  customDisallowedSearchParams: string[] | undefined,
): PathWithPathname => {
  // Explicitly want to allow empty strings
  // TODO(@seller-engineering): Audit empty string links and flag to teams.
  if (nextLocation?.pathname === undefined && nextLocation?.pathname === null) {
    throw new DevError('Navigation must have a properly formatted `pathname`');
  }

  const { pathname, search, hash } = nextLocation?.pathname
    ? parseUrlString(
        `${nextLocation?.pathname}${nextLocation?.search ? `?${nextLocation?.search}` : ''}`,
      )
    : // If pathname is not provided, use current pathname
      { ...nextLocation, pathname: currentLocation.pathname };

  // Merge search params - Only append allowed search params to url
  const newSearchParams = new URLSearchParams(search);
  const searchParams = new URLSearchParams(currentLocation.search);
  const DISALLOWED_SEARCH_PARAMS = customDisallowedSearchParams ?? DEFAULT_DISALLOWED_SEARCH_PARAMS;
  newSearchParams.forEach((value, key) => {
    if (key && value) {
      // Replace with new search value if search param already exists
      if (searchParams.get(key)) {
        searchParams.set(key, newSearchParams.get(key) ?? searchParams.get(key) ?? '');
      } else {
        // Append ALL new search params
        searchParams.append(key, value);
      }
    }
  });
  // Creates a read-only copy of searchParams
  const readOnlySearchParams = Object.freeze(
    Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, string>),
  );
  // Strips all disallowed search params
  Object.keys(readOnlySearchParams).forEach((key) => {
    if (DISALLOWED_SEARCH_PARAMS.includes(key)) {
      searchParams.delete(key);
    }
  });

  const searchString = searchParams.toString().trim();

  return {
    pathname,
    // A URL search string, beginning with a ?.
    search: searchString.length > 0 ? `?${searchString}` : '',
    hash,
  };
};

/**
 * Returns a helper function to calculate the next location object
 * Preserves query params and appends the addressUUID to the pathname if necessary
 */
export const useToPath = (
  customDisallowedSearchParams: string[] | undefined,
): ((to: To) => PathWithPathname) => {
  const ctx = useSafeQualificationContext();
  const location = useLocation();

  return useCallback(
    (to: To) =>
      getToPath(
        location,
        typeof to === 'string'
          ? { pathname: generatePath(to, { addressUUID: ctx?.addressUUID }) }
          : ({
              ...to,
              pathname: to?.pathname
                ? generatePath(to.pathname, { addressUUID: ctx?.addressUUID ?? null })
                : undefined,
            } as PathWithPathname),
        customDisallowedSearchParams,
      ),
    // eslint-disable-next-line @opendoor/no-unstable-hook-deps -- caller's responsibility
    [ctx?.addressUUID, customDisallowedSearchParams, location],
  );
};

const useSafeQualificationContext = (): IQualificationContext | undefined => {
  try {
    return useQualificationContext();
  } catch {
    // Do nothing if context is not available
  }
};
